import { useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { toast } from "sonner";
import api, { registerTimeRequiredHandler, formatApiError } from "@/api/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Global prompt shown when a task is marked complete without tracked time.
export const TimeRequiredDialog = () => {
  const [req, setReq] = useState(null);
  const [h, setH] = useState("");
  const [m, setM] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const resolver = useRef(null);

  useEffect(() => {
    registerTimeRequiredHandler((detail) => new Promise((resolve) => {
      resolver.current = resolve;
      setH(""); setM(""); setNote("");
      setReq(detail);
    }));
    return () => registerTimeRequiredHandler(null);
  }, []);

  const close = (ok) => {
    resolver.current?.(ok);
    resolver.current = null;
    setReq(null);
  };

  const minutes = (Number(h) || 0) * 60 + (Number(m) || 0);
  const submit = async (e) => {
    e.preventDefault();
    if (minutes < 1) { toast.error("Enter at least 1 minute"); return; }
    setSaving(true);
    try {
      await api.post(`/tasks/${req.task_id}/time/manual`, { hours: minutes / 60, note: note || "Logged while marking complete", for_user_id: req.user_id || null });
      toast.success("Time saved — completing task");
      close(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save time");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && close(false)}>
      <DialogContent className="bg-surface border-white/10 text-white max-w-md" data-testid="time-required-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Clock size={18} className="text-[#E11D2E]" /> Time spent is required</DialogTitle>
            <DialogDescription className="text-white/60">
              “{req?.title}” has no tracked time. Enter how long it took to mark it complete.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 my-5">
            <label className="text-sm font-medium">Hours
              <Input type="number" min="0" max="12" value={h} onChange={(e) => setH(e.target.value)} autoFocus
                data-testid="time-required-hours" className="mt-1.5 bg-surface border-white/15" placeholder="0" />
            </label>
            <label className="text-sm font-medium">Minutes
              <Input type="number" min="0" max="59" value={m} onChange={(e) => setM(e.target.value)}
                data-testid="time-required-minutes" className="mt-1.5 bg-surface border-white/15" placeholder="0" />
            </label>
            <label className="text-sm font-medium col-span-2">Note (optional)
              <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="time-required-note"
                className="mt-1.5 bg-surface border-white/15" placeholder="e.g. forgot to start the timer" />
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => close(false)} data-testid="time-required-cancel"
              className="border-white/15 bg-transparent text-white hover:bg-white/5">Cancel</Button>
            <Button type="submit" disabled={saving || minutes < 1} data-testid="time-required-submit"
              className="bg-[#E11D2E] hover:bg-[#F03041] text-white">Save time & complete</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
